import type { SetSyncStore } from "./syncTypes";
import type { SyncStoreState } from "./syncTypes";
import type { LocalSyncDb } from "./syncTypes";
import { produce } from "solid-js/store";
import { createEffect, on } from "solid-js";
import Fuse from "fuse.js";
import type { Concept } from "@storytell/cp-client/src/gen/sync/v3/sync_service_pb";

export type ConceptRecord = Concept & { id: string };

export const createConceptsManager = (db: LocalSyncDb | null, state: SyncStoreState, setState: SetSyncStore) => {
  // TODO: @andi, implement fuzzy search in a web-worker to avoid blocking the main thread
  // Set up fuzzy search for concepts
  const fuzzy = new Fuse<ConceptRecord>([], {
    keys: [{ name: "name", weight: 1 }],
  });

  const get = (id: string) => state.concept.records[id] as ConceptRecord | undefined;
  const getAll = () => Object.values(state.concept.records);
  const getMeta = () => state.concept.meta;

  // TODO: @andi, gracefully handle async errors for db operations
  // For now we just assume they'll work...
  const set = (id: string, record: ConceptRecord) => {
    db?.conceptRecord.put(record);
    setState("concept", "records", id, record);
    requestAnimationFrame(() => {
      fuzzy.remove((r) => r.id === id);
      fuzzy.add(record);
    });
  };

  const clear = () => {
    db?.conceptRecord.clear();
    setState("concept", "records", {});
    requestAnimationFrame(() => {
      fuzzy.setCollection([]);
    });
  };

  const addBulk = (records: ConceptRecord[]) => {
    db?.conceptRecord.bulkPut(records);
    setState(
      "concept",
      "records",
      produce((existing) => {
        for (const record of records) {
          existing[record.id] = record;
        }
      }),
    );
    requestAnimationFrame(() => {
      const ids = records.map((r) => r.id);
      fuzzy.remove((r) => ids.includes(r.id));
      for (const record of records) {
        fuzzy.add(record);
      }
    });
  };

  const setMeta = (
    meta:
      | SyncStoreState["concept"]["meta"]
      | ((meta: SyncStoreState["concept"]["meta"]) => SyncStoreState["concept"]["meta"]),
  ) => {
    const newMeta = typeof meta === "function" ? meta(state.concept.meta) : meta;
    db?.syncMeta.put(newMeta);
    setState("concept", "meta", newMeta);
  };

  const remove = (id: string) => {
    db?.conceptRecord.delete(id);
    setState(
      "concept",
      "records",
      produce((records) => delete records[id]),
    );
    requestAnimationFrame(() => {
      fuzzy.remove((record) => record.id === id);
    });
  };

  let onHydrated = false;
  createEffect(
    on(
      () => state.initializing,
      (initializing) => {
        if (initializing) return;
        if (onHydrated) return;
        onHydrated = true;

        requestAnimationFrame(() => {
          fuzzy.setCollection(Object.values(state.concept.records));
        });
      },
    ),
  );

  return { get, getAll, set, clear, addBulk, remove, fuzzy, getMeta, setMeta };
};

export const hydrateConceptsFromDb = async (db: LocalSyncDb | null, setState: SetSyncStore) => {
  if (!db) return;
  const allConceptRecords = await db.conceptRecord.toArray();
  const meta = await db.syncMeta.get("concepts");
  const obj: Partial<SyncStoreState["concept"]> = {
    records: allConceptRecords.reduce(
      (acc, record) => {
        acc[record.id] = record;
        return acc;
      },
      {} as Record<string, ConceptRecord>,
    ),
  };
  if (meta) {
    obj.meta = meta;
  }
  setState("concept", {
    ...obj,
  });
};
